/**
 * Hook for managing position filter state synchronized with URL query parameters.
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type {
  PositionFilterState,
  PositionStatusFilter,
  DateFieldFilter,
} from '@/types';
import { DEFAULT_FILTERS } from '@/types';

/**
 * Hook that manages position filter state via URL search params.
 * Enables deep linking and browser history for filter states.
 */
export function usePositionFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse filter state from URL params
  const filters: PositionFilterState = useMemo(() => {
    const symbol = searchParams.get('symbol') ?? DEFAULT_FILTERS.symbol;
    const statusParam = searchParams.get('status');
    const status: PositionStatusFilter =
      statusParam === 'ACTIVE' || statusParam === 'CLOSED'
        ? statusParam
        : DEFAULT_FILTERS.status;
    const dateFieldParam = searchParams.get('dateField');
    const dateField: DateFieldFilter =
      dateFieldParam === 'created_at' || dateFieldParam === 'closed_at'
        ? dateFieldParam
        : DEFAULT_FILTERS.dateField;
    const startDate = searchParams.get('startDate');
    const endDate = searchParams.get('endDate');

    return {
      symbol,
      status,
      dateField,
      startDate,
      endDate,
    };
  }, [searchParams]);

  // Update a single filter value
  const setFilter = useCallback(
    <K extends keyof PositionFilterState>(
      key: K,
      value: PositionFilterState[K]
    ) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);

        // Remove param if value is default/empty
        if (
          value === null ||
          value === '' ||
          value === DEFAULT_FILTERS[key]
        ) {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }

        return next;
      });
    },
    [setSearchParams]
  );

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      filters.symbol !== DEFAULT_FILTERS.symbol ||
      filters.status !== DEFAULT_FILTERS.status ||
      filters.startDate !== DEFAULT_FILTERS.startDate ||
      filters.endDate !== DEFAULT_FILTERS.endDate
    );
  }, [filters]);

  return {
    filters,
    setFilter,
    clearFilters,
    hasActiveFilters,
  };
}
